import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Users, BarChart2, ArrowUpRight, Clock, CheckCircle, XCircle, Zap, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { apiFetch } from '../App';

const T = { bg2:'#0d1114', border:'rgba(255,255,255,.06)', text:'#e8eaeb', muted:'#4a5a63', green:'#00c566', yellow:'#f5c842', orange:'#ff6b35' };

function Card({ children, style }) {
  return <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:16, padding:20, ...style }}>{children}</div>;
}

function StatCard({ Icon, label, value, sub, color='#00c566' }) {
  return (
    <Card>
      <div style={{ width:38, height:38, borderRadius:10, background:`rgba(${color==='#00c566'?'0,197,102':color==='#f5c842'?'245,200,66':'255,107,53'},.08)`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12 }}>
        <Icon size={17} color={color} strokeWidth={2}/>
      </div>
      <div style={{ fontSize:26, fontWeight:800, color:T.text, marginBottom:2 }}>{value}</div>
      <div style={{ fontSize:12, color:T.muted }}>{label}</div>
      {sub && <div style={{ fontSize:11, color:T.green, marginTop:4 }}>{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const chartData = Array.from({length:14},(_,i)=>({ d:`D${i+1}`, vol:Math.floor(Math.random()*500+100), earn:Math.floor(Math.random()*25+5) }));

  return (
    <div>
      {/* Beta Banner */}
      <div style={{ background:'rgba(245,200,66,.06)', border:'1px solid rgba(245,200,66,.15)', borderRadius:12, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:10 }}>
        <AlertCircle size={15} color="#f5c842" strokeWidth={2} style={{ flexShrink:0 }}/>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#f5c842' }}>Creator Portal — Beta</div>
          <div style={{ fontSize:11, color:T.muted }}>Analytics are placeholder. Live data arrives with mainnet launch.</div>
        </div>
        <a href="https://naijapredict.net" target="_blank" rel="noreferrer" style={{ fontSize:12, color:T.green, fontWeight:600, whiteSpace:'nowrap' }}>Visit App →</a>
      </div>

      <h1 style={{ fontSize:'clamp(20px,4vw,28px)', fontWeight:800, marginBottom:4 }}>Dashboard</h1>
      <p style={{ fontSize:14, color:T.muted, marginBottom:24 }}>Overview of your creator performance</p>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:20 }}>
        <StatCard Icon={BarChart2}  label="Total Markets"    value="0"      color="#00c566"/>
        <StatCard Icon={DollarSign} label="Total Volume"     value="$0.00"  color="#f5c842"/>
        <StatCard Icon={Zap}        label="Royalties"        value="$0.00"  color="#00c566" sub="0.5% of volume"/>
        <StatCard Icon={Users}      label="Active Traders"   value="0"      color="#ff6b35"/>
      </div>

      {/* Charts */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16, marginBottom:20 }}>
        <Card>
          <div style={{ fontSize:15, fontWeight:700, marginBottom:4 }}>Trading Volume</div>
          <div style={{ fontSize:12, color:T.muted, marginBottom:16 }}>Last 14 days</div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#00c566" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#00c566" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="d" tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false} width={30}/>
              <Tooltip contentStyle={{background:'#0d1114',border:'1px solid rgba(255,255,255,.08)',borderRadius:8,fontSize:12}}/>
              <Area type="monotone" dataKey="vol" stroke="#00c566" strokeWidth={2} fill="url(#g1)" dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <div style={{ fontSize:15, fontWeight:700, marginBottom:4 }}>Daily Earnings</div>
          <div style={{ fontSize:12, color:T.muted, marginBottom:16 }}>Royalties (USDC)</div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData}>
              <XAxis dataKey="d" tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false} width={30}/>
              <Tooltip contentStyle={{background:'#0d1114',border:'1px solid rgba(255,255,255,.08)',borderRadius:8,fontSize:12}}/>
              <Line type="monotone" dataKey="earn" stroke="#f5c842" strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent Markets */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ fontSize:15, fontWeight:700 }}>Recent Markets</div>
          <a href="/markets" style={{ fontSize:13, color:T.green, fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>View all <ArrowUpRight size={13}/></a>
        </div>
        <div style={{ textAlign:'center', padding:'32px 0', color:T.muted }}>
          <BarChart2 size={30} style={{ marginBottom:8, opacity:.4 }}/>
          <div style={{ fontSize:14 }}>No markets yet</div>
          <div style={{ fontSize:12, marginTop:4 }}>Create your first market to start earning</div>
        </div>
      </Card>
    </div>
  );
}
